import { useEffect, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import {
  selectTrainingType,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import {
  selectAbandonedTours,
  selectAbortedThisSession,
  selectActiveTourId,
  selectCompletedTours,
  selectIsOnboardingHydrated,
  selectWelcomeChoice,
  setWelcomeChoice,
  setTourStarted,
} from "../../app/slices/OnboardingSlice";
import TourRunner from "./internal/TourRunner";
import { OnboardingStrategy360 } from "./strategies/OnboardingStrategy360";
import { OnboardingStrategy3D } from "./strategies/OnboardingStrategy3D";
import { OnboardingStrategyDashboard } from "./strategies/OnboardingStrategyDashboard";
import type { OnboardingStrategy, OnboardingTour } from "./onboardingTypes";
import OnboardingWelcome from "./OnboardingWelcome";

/**
 * The single mount point for onboarding, replacing AutoOnboarding.
 *
 * The old component was mounted unconditionally for both training types
 * (TrainingEditor.tsx:306) against a single `visited_trainingEditor` flag, so a
 * 3D author burned the flag for a tour whose every target was 360-only. Here
 * the strategy picks the content and each tour owns its own completion state.
 */

/**
 * An exhaustive switch rather than a ternary, deliberately.
 *
 * The ternary this replaces returned the 360 strategy for anything that was
 * not 3D, and trainingType is NotSet on every route outside an open training.
 * So the host on the dashboard started editor360StageA, could not resolve a
 * single anchor, and recorded the abort for the rest of the session, which
 * suppressed the real 360 tour the moment the author opened a training. A
 * switch also makes the next training type a compile time decision instead of
 * a silent fallthrough.
 */
export function useOnboardingStrategy(): OnboardingStrategy {
  const trainingType = useSelector(selectTrainingType);
  return useMemo(() => {
    switch (trainingType) {
      case TrainingType.Training3D:
        return OnboardingStrategy3D;
      case TrainingType.Training360:
        return OnboardingStrategy360;
      case TrainingType.NotSet:
      default:
        return OnboardingStrategyDashboard;
    }
  }, [trainingType]);
}

/**
 * Picks the first tour that is allowed to run right now and has not already had
 * its turn. Returns null when there is nothing to do.
 *
 * "Had its turn" covers three different endings, and all three must be checked
 * here or the host restarts the tour on its next render:
 *  - completed: the user finished it,
 *  - aborted: an anchor did not resolve (this session only),
 *  - abandoned: the user closed or skipped it. Left out originally, which made
 *    the close button restart the tour from step one instead of ending it.
 */
export function selectNextTour(
  strategy: OnboardingStrategy,
  runnable: readonly boolean[],
  completed: readonly string[],
  aborted: readonly string[],
  abandoned: Readonly<Record<string, number | undefined>> = {}
): OnboardingTour | null {
  for (let i = 0; i < strategy.tours.length; i++) {
    const tour = strategy.tours[i];
    if (completed.includes(tour.id)) continue;
    if (aborted.includes(tour.id)) continue;
    if (abandoned[tour.id] !== undefined) continue;
    if (!runnable[i]) continue;
    return tour;
  }
  return null;
}

export default function OnboardingHost() {
  const dispatch = useDispatch();
  const store = useStore();
  const strategy = useOnboardingStrategy();

  const isHydrated = useSelector(selectIsOnboardingHydrated);
  const activeTourId = useSelector(selectActiveTourId);
  const welcomeChoice = useSelector(selectWelcomeChoice);
  const completedTours = useSelector(selectCompletedTours);
  const abortedThisSession = useSelector(selectAbortedThisSession);
  // A stored reference, not a fresh object per call: a new identity here would
  // re-run the memo below on every dispatch.
  const abandonedTours = useSelector(selectAbandonedTours);

  /**
   * canRun predicates are arbitrary, so this has to run against whole state —
   * but collapsing the results to a bitstring keeps it a primitive. useSelector
   * then re-renders only when a tour's eligibility actually flips, not on every
   * dispatched action.
   */
  const runnableSignature = useSelector((s: any) =>
    strategy.tours.map((tour) => (tour.canRun(s) ? "1" : "0")).join("")
  );

  const nextTour = useMemo(() => {
    if (!isHydrated) return null;
    const runnable = runnableSignature.split("").map((flag) => flag === "1");
    return selectNextTour(
      strategy,
      runnable,
      completedTours,
      abortedThisSession,
      abandonedTours
    );
  }, [
    isHydrated,
    strategy,
    runnableSignature,
    completedTours,
    abortedThisSession,
    abandonedTours,
  ]);

  useEffect(() => {
    if (welcomeChoice !== "started") return;
    if (activeTourId !== null) return;
    if (!nextTour) return;

    // Read state lazily: this only matters at the moment a tour starts, so
    // there is no reason to subscribe to it.
    const stepIndex = nextTour.initialStepIndex?.(store.getState()) ?? 0;

    dispatch(
      setTourStarted({
        tourId: nextTour.id,
        stepIndex: Math.min(stepIndex, nextTour.steps.length - 1),
      })
    );
  }, [activeTourId, dispatch, nextTour, store, welcomeChoice]);

  const runningTour = useMemo(
    () => strategy.tours.find((tour) => tour.id === activeTourId) ?? null,
    [strategy, activeTourId]
  );

  if (
    isHydrated &&
    welcomeChoice === "unseen" &&
    activeTourId === null &&
    nextTour !== null
  ) {
    return (
      <OnboardingWelcome
        onStart={() => dispatch(setWelcomeChoice("started"))}
        onDismiss={() => dispatch(setWelcomeChoice("dismissed"))}
      />
    );
  }

  if (!runningTour) return null;

  return <TourRunner tour={runningTour} />;
}
